/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, OnInit, ViewChild, ElementRef, OnDestroy
} from '@angular/core';
import { ParamMap, ActivatedRoute, Router } from '@angular/router';
import { Subscription, zip, combineLatest, Observable, iif } from 'rxjs';
import { first, map, switchMap, tap, take, filter } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';

import { ANIMATIONS } from '@app/constants/animations.constants';
import { AppFacade } from '@app/app.facade';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { FlightType } from '@search-bar/entities/flight-type';
import { FlightClassTypes } from '@shared/base-search-bar/entities/flight-class-types';
import { environment } from '@environment';
import { FlexibleDatesMappedResult } from '@flight-results/entities/flexible.dates';
import { DateTransformPipe } from '@app/pipes/date-format.pipe';
import { CapitalizeAllPipe } from '@app/pipes/capitalize-all.pipe';
import {
  Slide, ByPriceOption, ByDate, FLEX_CARD_TYPE
} from '@flight-results/components/mobile-flexible-dates/entities/mobile-flexible-dates.entities';
import { mapSearchData } from '@search-bar/mappers/url-search.mapper';
import { flightResultsRootRoute, flexiDatesRoute } from '@flight-results/flight-results-routing.constants';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { getDaysBetweenDates, getUTCDate } from '@flight-results/entities/date-validation';
import { SearchType } from '@search-bar/entities/search-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { ChipActionStatus } from '@design-system/components/molecules/chip-action/entities/chip-action.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import {
  TableTitleData, TableTitleRow, TableTitleSelection
} from '@design-system/components/molecules/table-title/entities/table-title.types';
import { CapitalizePipe } from '@app/pipes/capitalize.pipe';
import { HeaderType } from '@design-system/components/templates/header/entities/header.types';
import { HeaderProductType } from '@header/entities/ds-header-config';

@Component({
  selector: 'app-mobile-flexible-dates',
  templateUrl: './mobile-flexible-dates.component.html',
  styleUrls: ['./mobile-flexible-dates.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MobileFlexibleDatesComponent implements OnInit, OnDestroy {

  @ViewChild('content') content: ElementRef;
  @ViewChild('boxContainer') boxContainer: ElementRef;

  public CHEAPEST_DATES: FLEX_CARD_TYPE = FLEX_CARD_TYPE.CHEAPEST_DATES;
  public SAME_DURATION: FLEX_CARD_TYPE = FLEX_CARD_TYPE.SAME_DURATION;
  public KEEP_DEPARTURE: FLEX_CARD_TYPE = FLEX_CARD_TYPE.KEEP_DEPARTURE;
  public KEEP_ARRIVAL: FLEX_CARD_TYPE = FLEX_CARD_TYPE.KEEP_ARRIVAL;
  public animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public flightType: FlightType;
  public flightClass: FlightClassTypes;
  public currency: string = environment.countryInfo.currency_code;
  public departureDate: Date;
  public arrivalDate: Date;
  public animationRunning: boolean;
  public slides: Slide[] = [];
  public searchType: string = SearchType.FLIGHT_RESULTS;
  public source: string;
  public isSelectedCurrentDate = false;
  
  private readonly offsetBoxContainers: number = 28;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  public readonly dsHeaderConfig = {
    type: HeaderType.Results,
    productType: HeaderProductType.Air,
    pageType: null,
    titleKey: null,
  };
  
  private subscriptions: Subscription = new Subscription();
  private adults: number = 0;
  private children: number = 0;
  private infants: number = 0;
  private baggageIncluded: boolean = false;
  private departure: string;
  private arrival: string;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private readonly appFacade: AppFacade,
    private readonly flightResultsFacade: FlightResultsFacade,
    private readonly baseSearchFacade: BaseSearchBarFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly transloco: TranslocoService,
    private readonly currencyFormatPipe: CurrencyFormatPipe,
    private readonly dateFormatPipe: DateTransformPipe,
    private readonly datePipe: DatePipe,
    private readonly capitalizeAllPipe: CapitalizeAllPipe,
    private readonly domManipulateService: DomManipulateService,
    private readonly amplitude: PropertiesAmplitudeService,
    private readonly avoService: AvoService,
    private readonly capitalizePipe: CapitalizePipe,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.combineSubscription());
    this.subscriptions.add(
      combineLatest([
        iif(
          () => this.isRoundTrip, this.byPrice$, this.byPriceOneWay$
        ),
        this.isLoadingFlexDates$,
      ]).pipe(
        first(([data, isLoading]) => !isLoading && !isEmpty(data)),
        take(1),
        switchMap(([data]) => this.amplitude.searchProperties$(SearchBy.FLIGHTS).pipe(
          take(1),
          map(searchProps => ({
            eventName: AVO_EVENTS.VIEW_FLEXDATES, searchProps,
            eventParams: { PRICE_DIFFERENCE: [...data].map(x => x.PriceDifference).reduce((a, b) => a < b ? a : b, 0) },
          })),
          switchMap(({ eventName, eventParams, searchProps }) =>
            this.avoService.getEventTriggerProps$(eventName, eventParams, searchProps).pipe(
              take(1),
              tap(triggerviewFlexdatesMobile => {
                this.AvoDispatch.viewFlexdates({
                  ...this.avoService.avoBaseProperties(triggerviewFlexdatesMobile),
                  ...this.avoService.avoSearchProps(triggerviewFlexdatesMobile?.SEARCH_PROPS),
                  priceDifference: triggerviewFlexdatesMobile?.EVENT_PROPS?.PRICE_DIFFERENCE as number
                });
              })
            ))
        ))
      ).subscribe()
    );
    this.domManipulateService.setBodyScroll(false);
    this.appFacade.historyPush(flexiDatesRoute);
    this.baseSearchFacade.toggleHeaderEditButtonVisible(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.domManipulateService.setBodyScroll(true);
    this.appFacade.historyPop();
    this.baseSearchFacade.setSkipServices(false);
    this.baseSearchFacade.toggleHeaderEditButtonVisible(true);
    if (this.isSelectedCurrentDate && this.source === EVENT_SOURCE.TRAVEL_HOME) {
      this.baseSearchFacade.forceCallWithoutParam();
    }
  }

  get headerParams$(): Observable<{ [key: string]: string }> {
    return this.baseSearchFacade.searchData$.pipe(
      map((data) => {
        const urlParams = mapSearchData(data);
        const params = urlParams?.split('/') ?? [];
        return {
          flight_type: params?.[0],
          flight_class: params?.[1],
          departure: params?.[2],
          arrival: params?.[3],
          departure_date: params?.[4],
          arrival_date: this.isRoundTrip ? params?.[5] : null,
          adults: this.isRoundTrip ? params?.[6] : params?.[5],
          children: this.isRoundTrip ? params?.[7] : params?.[6],
          infants: this.isRoundTrip ? params?.[8] : params?.[7],
          airline: this.isRoundTrip ? params?.[9] : params?.[8],
          baggage_included: this.isRoundTrip ? params?.[10] : params?.[9],
        };
      })
    );
  }
  get daysBetweenDates(): number {
    return getDaysBetweenDates(this.departureDate, this.arrivalDate); 
  }
  get flexDates$(): Observable<FlexibleDatesMappedResult> {
    return this.flightResultsFacade.flexDatesMapped$; 
  }
  get isLoadingFlexDates$(): Observable<boolean> {
    return combineLatest([this.flightResultsFacade.isLoadingFlexDates$, this.flexDates$])
      .pipe(map(([isLoadingFlexDates, flexDates]) => isLoadingFlexDates && !!flexDates));
  }
  get isRoundTrip(): boolean {
    return this.flightType === FlightType.ROUNDTRIP; 
  }
  get byPrice$(): Observable<ByPriceOption[]> {
    return this.flexDates$.pipe(map(f => f?.ByPrice)); 
  }
  get byLengthOfStay$(): Observable<ByPriceOption[]> {
    return this.flexDates$.pipe(map(f => f?.ByLengthOfStay)); 
  }

  get byFixingDepartureAndChangingReturnTrip$(): Observable<ByDate[]> {
    return this.flexDates$.pipe(map(f => f.ByFixingDepartureAndChangingReturnTrip?.map(r => {
      return {
        ...r,
        DateRange: this.getDateRangeMessage(r.DepartureDate, r.ArrivalDate),
        DaysDifferenceMessage: this.getDaysDifferenceMessage(this.arrivalDate, getUTCDate(r.ArrivalDate))
      };
    })));
  }

  get byFixingReturnAndChangingDepartureDate$(): Observable<ByDate[]> {
    return this.flexDates$.pipe(map(f => f?.ByFixingReturnAndChangingDepartureDate?.map(r => {
      return {
        ...r,
        DateRange: this.getDateRangeMessage(r.DepartureDate, r.ArrivalDate),
        DaysDifferenceMessage: this.getDaysDifferenceMessage(this.departureDate, getUTCDate(r.DepartureDate))
      };
    })));
  }

  get byPriceOneWay$(): Observable<ByDate[]> {
    return this.flexDates$.pipe(map(f => f?.ByPriceOneWay?.map(r => {
      return {
        ...r,
        DateRange: this.capitalizeAllPipe.transform(this.dateFormatPipe.transform(
          this.datePipe.transform(new Date(r.DepartureDate), 'EEE, dd MMM'))),
        DaysDifferenceMessage: this.getDaysDifferenceMessage(this.departureDate, getUTCDate(r.DepartureDate))
      };
    })));
  }

  public getTableData$(cardType: FLEX_CARD_TYPE): Observable<TableTitleData> {
    const options$ = cardType === FLEX_CARD_TYPE.CHEAPEST_DATES ? (this.isRoundTrip ? this.byPrice$ : this.byPriceOneWay$) :
      cardType === FLEX_CARD_TYPE.KEEP_ARRIVAL ? this.byLengthOfStay$ :
        cardType === FLEX_CARD_TYPE.SAME_DURATION ? this.byFixingDepartureAndChangingReturnTrip$ :
          this.byFixingReturnAndChangingDepartureDate$;
    return options$.pipe(
      map((options) => {
        const tableTitle = `FLIGHT_RESULTS_MODULE.FLEX_DATES.TABS.${
          cardType === FLEX_CARD_TYPE.CHEAPEST_DATES ? 'CHEAPEST_DATES' :
            cardType === FLEX_CARD_TYPE.KEEP_ARRIVAL ?'TRIP_OF' :
              cardType === FLEX_CARD_TYPE.SAME_DURATION ? 'KEEP_DEPARTURE_CHANGE_ROUNDTRIP' :
                'KEEP_RETURN_CHANGE_DEPARTURE'
        }`;
        return {
          Title: this.transloco.translate(tableTitle, { days: this.daysBetweenDates }),
          Columns: [
            this.isRoundTrip && (cardType === FLEX_CARD_TYPE.CHEAPEST_DATES || cardType === FLEX_CARD_TYPE.KEEP_ARRIVAL) && {
              Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.DEPARTURE_TRIP'),
              Rows: options?.map(option => this.getDateValues(option.DepartureDate, option.IsCurrentDate)) ?? []
            },
            this.isRoundTrip && (cardType === FLEX_CARD_TYPE.CHEAPEST_DATES || cardType === FLEX_CARD_TYPE.KEEP_ARRIVAL) && {
              Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.RETURN_TRIP'),
              Rows: options?.map(option => this.getDateValues(option.ArrivalDate, option.IsCurrentDate)) ?? []
            },
            (
              (cardType === FLEX_CARD_TYPE.CHEAPEST_DATES && !this.isRoundTrip) ||
              (this.isRoundTrip && cardType === FLEX_CARD_TYPE.SAME_DURATION || cardType === FLEX_CARD_TYPE.KEEP_DEPARTURE)
            ) && {
              Title: this.transloco.translate(`FLIGHT_RESULTS_MODULE.FLEX_DATES.${
                cardType === FLEX_CARD_TYPE.SAME_DURATION ? 'RETURNING' : 'DEPARTING'
              }`),
              Rows: options?.map((option: ByDate) => ({
                Title: option.DaysDifferenceMessage,
                Subtitle: option.DateRange,
                IsRecommended: option.IsCurrentDate,
              })) ?? []
            },
            {
              Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.FROM', { currency: this.currency }),
              Rows: options?.map(option => this.getPriceValues(
                option.TotalPrice, option.PriceDifference, option.CurrencyCode, option.IsCurrentDate
              )) ?? []
            }
          ]?.filter(column => !isEmpty(column)),
          EmitValue: true,
        };
      })
    );
  }

  public getChipStatus(isActive: boolean): ChipActionStatus {
    return isActive ? ChipActionStatus.Selected : ChipActionStatus.Default;
  }

  public selectedColumnAndRow(points: TableTitleSelection, cardType: FLEX_CARD_TYPE): void {
    const options$ = cardType === FLEX_CARD_TYPE.CHEAPEST_DATES ? this.byPrice$ :
      cardType === FLEX_CARD_TYPE.KEEP_ARRIVAL ? this.byLengthOfStay$ :
        cardType === FLEX_CARD_TYPE.SAME_DURATION ? this.byFixingDepartureAndChangingReturnTrip$ :
          this.byFixingReturnAndChangingDepartureDate$;
    options$.pipe(
      filter(options => !isEmpty(options)),
      take(1),
      tap((options) => {
        const option = options?.[points.Row];
        this.selectOption(option?.OptionId, option?.PriceDifference, cardType, option.IsCurrentDate);
      })
    ).subscribe().unsubscribe();
  }

  public getDiscountMessage(priceDifference: number, currencyCode: string): string {
    return priceDifference < 0 ?
      this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.YOU_SAVE',
        { amount: this.currencyFormatPipe.transform(Math.abs(priceDifference), currencyCode) }) : '';
  }

  public moveSlider(position: number, currentPosition: number): void {
    let directionRight = true;
    if (position > currentPosition) {
      directionRight = true;
    } else {
      directionRight = false;
    }
    while (currentPosition !== position) {
      if (directionRight) {
        currentPosition++;
      } else currentPosition--;
      if (!this.content.nativeElement) return;
      if (currentPosition >= this.content.nativeElement.children.length) {
        position = currentPosition = 0;
      }
      this.scrollTo(this.content.nativeElement.children[currentPosition]?.offsetLeft - this.offsetBoxContainers);
    }
  }

  public onScroll(): void {
    if (this.animationRunning) return;
    this.slides.map(s => s.Active = false);
    this.slides[Math.round(this.content.nativeElement.scrollLeft / this.content.nativeElement.clientWidth)].Active = true;
  }

  public goToSlide(index: number): void {
    const currentIndex = this.slides.map(e => e.Active).indexOf(true);
    this.slides.map(s => s.Active = false);
    this.slides[index].Active = true;
    this.moveSlider(index, currentIndex);
  }

  public close(): void {
    this.appFacade.navigateToLastRoute();
  }

  // eslint-disable-next-line @typescript-eslint/typedef
  public selectOption(optionId: number, priceDifference = 0, cardType: FLEX_CARD_TYPE, isCurrentDate: boolean): void {
    this.isSelectedCurrentDate = isCurrentDate;
    zip(
      this.flexDates$,
      this.amplitude.searchProperties$(SearchBy.FLIGHTS).pipe(
        switchMap(searchProps =>
          this.avoService.getEventTriggerProps$(AVO_EVENTS.SELECT_FLEXDATE_OPTION, {
            PRICE_DIFFERENCE: priceDifference, CARD_TYPE: cardType
          }, searchProps)
        ))
    ).pipe(
      map(([flexDates, trigger]) => ({ flexDates, trigger })),
      tap(({ trigger }) => {
        this.AvoDispatch.selectFlexdateOption({
          ...this.avoService.avoBaseProperties(trigger),
          ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
          priceDifference: trigger?.EVENT_PROPS?.PRICE_DIFFERENCE as number,
          cardType: trigger?.EVENT_PROPS?.cardType as string,
        });
      }),
      tap(({ flexDates }) => {
        const result = flexDates.Results.find(f => f.OptionId === optionId);
        const branchCode = flexDates.BranchCode;
        const departureDate = getUTCDate(result.Sectors[0].Flights[0].DepartureDate);
        const departureTerminalType = result.Sectors[0].Flights[0].DepartureTerminalType.toLowerCase();
        const arrivalDate = getUTCDate(result.Sectors[result.Sectors.length - 1].Flights[0].DepartureDate);
        const arrivalTerminalType = result.Sectors[0].Flights[0].ArrivalTerminalType.toLowerCase();
        const payload = {
          flightType: this.flightType,
          departureDate: departureDate,
          arrivalDate: arrivalDate,
          destinationPlace: { code: this.arrival },
          arrivalPlace: { code: this.arrival },
          departurePlace: { code: this.departure },
          passengerArrangement: [
            { id: PassengerType.ADULTS, quantity: this.adults },
            { id: PassengerType.CHILDREN, quantity: this.children },
            { id: PassengerType.INFANTS, quantity: this.infants }],
          baggageIncluded: this.baggageIncluded
        } as SearchData;
        const urlParams = mapSearchData(payload);
        this.router.navigate([`${flightResultsRootRoute}/${urlParams}`],
          { queryParams: { 
            source: EVENT_SOURCE.TAG,
            branch_code: branchCode,
            departure_type: departureTerminalType,
            arrival_type: arrivalTerminalType
          } });
      }),
    ).subscribe().unsubscribe();
  }

  private getDateRangeMessage(DepartureDate: string, ArrivalDate: string): string {
    return `${this.capitalizeAllPipe.transform(this.dateFormatPipe.transform(this.datePipe.transform(DepartureDate, 'dd MMM')))}`
      + `- ${this.capitalizeAllPipe.transform(this.dateFormatPipe.transform(this.datePipe.transform(ArrivalDate, 'dd MMM')))}`
      + ` (${this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.LASTS_DAYS',
        { days: getDaysBetweenDates(getUTCDate(DepartureDate), getUTCDate(ArrivalDate)) })})`;
  }

  private getDaysDifferenceMessage(currentDate: Date, optionalDate: Date): string {
    const diff = getDaysBetweenDates(optionalDate, currentDate);
    if (diff === 0) {
      return this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.YOUR_CURRENT_DATE');
    } else if (Math.abs(diff) === 1) {
      return diff === 1 ?
        this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.DAY_BEFORE', { days: Math.abs(diff) }) :
        this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.DAY_AFTER', { days: Math.abs(diff) });
    } else if (diff > 1) {
      return this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.DAYS_BEFORE', { days: diff });
    }
    return this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.DAYS_AFTER', { days: Math.abs(diff) });
  }

  private setSlides(): void {
    this.slides = [{
      Active: true,
      Icon: Icon.MoneyIcon,
      Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.I_WISH'),
      SubTitle: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.BE_CHEAP')
    },
    {
      Active: false,
      Icon: Icon.Calendar,
      Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.LASTS'),
      SubTitle: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.LASTS_DAYS', { days: this.daysBetweenDates })
    },
    {
      Active: false,
      Icon: Icon.Flight1,
      Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.WISH_DEPARTURE'),
      SubTitle: this.capitalizeAllPipe.transform(
        this.dateFormatPipe.transform(this.datePipe.transform(this.departureDate, 'EE dd MMM')))
    }, {
      Active: false,
      Icon: Icon.Flight2,
      Title: this.transloco.translate('FLIGHT_RESULTS_MODULE.FLEX_DATES.WISH_RETURN_DATE'),
      SubTitle: this.capitalizeAllPipe.transform(
        this.dateFormatPipe.transform(this.datePipe.transform(this.arrivalDate, 'EE dd MMM')))
    }];
  }

  private searchByParams(params: ParamMap): void {
    this.flightType = params.get('flight_type').toUpperCase() as FlightType;
    this.flightClass = params.get('flight_class').toUpperCase() as FlightClassTypes;
    this.departureDate = formatDateByRegex(params.get('departure_date'));
    this.arrivalDate = this.isRoundTrip ? formatDateByRegex(params.get('arrival_date')) : new Date();
    this.adults = +params.get('adults');
    this.children = +params.get('children');
    this.infants = +params.get('infants');
    this.baggageIncluded = params.get('baggage_included').toUpperCase() === 'TRUE';
    this.departure = params.get('departure');
    this.arrival = params.get('arrival');
  }

  private combineSubscription(): Subscription {
    return combineLatest([this.route.parent.paramMap, this.route.parent.queryParamMap])
      .subscribe(([ params, queries ]) => {
        this.source = queries.get(SOURCE_PARAM);
        this.searchByParams(params);
        this.setSlides();
      });
  }

  private scrollTo(position: number): void {
    this.animationRunning = true;
    const start = this.content.nativeElement.scrollLeft;
    const change = position - start;
    let currentTime = 0;
    const increment = 20;
    const duration = 200;
    const animateScroll = () => {
      currentTime += increment;
      const val = this.easeInOutQuad(currentTime, start, change, duration);
      this.content.nativeElement.scrollLeft = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      } else {
        setTimeout(() => this.animationRunning = false, increment);
      }
    };
    animateScroll();
  }

  private easeInOutQuad(t: number, b: number, c: number, d: number): number {
    t /= d / 2;
    if (t < 1) return c / 2 * t * t + b;
    t--;
    return -c / 2 * (t * (t - 2) - 1) + b;
  }

  private getDateValues(date: string, isCurrentDate: boolean): TableTitleRow {
    return {
      Title: this.dateFormatPipe.transform(this.datePipe.transform(date, 'dd MMM')),
      Subtitle: this.capitalizePipe.transform(
        this.dateFormatPipe.transform(this.datePipe.transform(date, 'EEEE'))
      ),
      IsRecommended: isCurrentDate,
    };
  }

  private getPriceValues(
    totalPrice: number, priceDifference: number, currencyCode: string, isCurrentDate: boolean
  ): TableTitleRow {
    return {
      Title: this.currencyFormatPipe.transform(totalPrice, currencyCode),
      Subtitle: this.getDiscountMessage(priceDifference, currencyCode),
      IsRecommended: isCurrentDate,
      SubtitleIsHiHighlight: true,
    };
  }
}
